<template>
	<ParamWrapper v-bind="wrapperProps" hideTitle @input-data-closed="onDataInputMenuClose">
		<div class="add-number-optional-fields" v-if="numberOfOptionsLeft !== 0">{{ numberOfOptionsLeftText }}</div>
		<div class="display-flex flex-row more-options">
			<div class="add-optional-fields" @click="showMoreOptions = !showMoreOptions">+ add optional fields</div>
			<v-icon class="arrow">{{ showMoreOptions ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
		</div>
		<DefaultPropertyDropdownInput v-if="showMoreOptions" v-bind="componentProps" v-on="componentListeners" />
		<template v-slot:input-data>
			<slot name="input-data">
				<SelectDataInputMenu
					:param="param"
					:items="selectItems"
					ref="data-input-menu"
					v-on="dataInputListeners"
					:disableMappedData="true"
					@update-external-localValue="updateSelectValue"
				/>
			</slot>
		</template>
	</ParamWrapper>
</template>

<script>
import { mapActions } from 'vuex';
import { updateParamsMapKey } from '@/store/modules/selectedNode/actions';

import { pluralize } from '@/utils/language-service';
import ParamTypeHelper from './param-type-helper';

import ThisFlowMixin from './mixins/ThisFlowMixin';
import ParamTypeMixin from './mixins/ParamTypeMixin';
import ParamTypeExtenderDataInputMenuMixin from './mixins/ParamTypeExtenderDataInputMenuMixin';
import ExternalSourceMixin from './mixins/ExternalSourceMixin';
import DefaultPropertyDropdownInput from '../inputsComponents/DefaultPropertyDropdownInput';
import SelectDataInputMenu from '../DataInputMenus/SelectDataInputMenu';

import { SELECT_TAB_KEY } from '../../../DataInput/DataInputMenu/helpers/data-input-menu.const';

export default {
	name: 'OptionalParamsPickerParamType',
	mixins: [ParamTypeMixin, ParamTypeExtenderDataInputMenuMixin, ThisFlowMixin, ExternalSourceMixin],
	components: {
		SelectDataInputMenu,
		DefaultPropertyDropdownInput,
	},
	computed: {
		selectItems() {
			if (this.param.hasOwnProperty('options') && Array.isArray(this.param.options)) {
				return this.param.options.map(({ text: option, value, subText }) => ({ option, value, subText }));
			}
			return [];
		},
		componentConfig() {
			const EXTERNAL_VALUE_PROPS = {
				...this.$attrs,
				ref: 'richEditor',
				rules: this.fieldRules,
				multipleSelection: true,
				readonly: this.readonly,
				paramKey: this.param.key,
				isFocused: this.isFocused,
				value: this.localValueDisplay,
				showDataMenu: this.showDataMenu,
				isDropdownOpen: this.showDataMenu,
				inputFieldHint: this.param.placeholder,
			};
			const EXTERNAL_VALUE_LISTENERS = {
				blur: this.onInputBlur,
				focus: this.onInputFocus,
				click: this.onInputClick,
				open: this.openDataInputMenu,
				close: this.closeDataInputMenu,
				'clear-value': this.clearValue,
				'update-params-map': this.updateParamsMapKey,
			};

			const REGULAR_PROPERTY_DROPDOWN_INPUT_CONFIG = ParamTypeHelper.buildComponentConfigItem(
				DefaultPropertyDropdownInput,
				EXTERNAL_VALUE_PROPS,
				EXTERNAL_VALUE_LISTENERS
			);

			return REGULAR_PROPERTY_DROPDOWN_INPUT_CONFIG;
		},
		component() {
			return this.componentConfig.component;
		},
		componentProps() {
			return this.componentConfig.props;
		},
		componentListeners() {
			return this.componentConfig.listeners;
		},
		paramValueInParamsMap() {
			return this.paramsMap[this.param.key] || [];
		},
		numberOfOptionsLeft() {
			return this.param.options.length - this.paramValueInParamsMap.length || 0;
		},
		numberOfOptionsLeftText() {
			return `${this.numberOfOptionsLeft} other ${pluralize('field', this.numberOfOptionsLeft)} available`;
		},
	},

	methods: {
		...mapActions([updateParamsMapKey.fullPath]),
		clearValue() {
			this.updateSelectValue({ id: '', name: '' });
		},
		updateSelectValue(payload) {
			this.valuesFromDataInputMenuToUpdate = payload;
			this.updateConstantValueFromDataInputMenu(payload); // this will not send to backend.
		},
		updateParamsMapKey(value, resetParamsMapLabelForKey) {
			this[updateParamsMapKey.fullPath]({
				paramKey: this.param.key,
				resetParamsMapLabelForKey,
				value: { multi_select_type: this.localTab, ...value },
			});
		},
		addSelectTabItem() {
			const fieldName = this.param.name ?? 'fieldName';
			this.addTabItem(SELECT_TAB_KEY, fieldName, true);
		},
	},
	mounted() {
		this.addSelectTabItem();
	},
	data() {
		return {
			showMoreOptions: false,
			valuesFromDataInputMenuToUpdate: [],
		};
	},
	watch: {
		componentConfig: {
			handler() {
				this.$nextTick(() => {
					this.setInputElement(this.$refs['richEditor']?.$el);
				});
			},
		},
		showDataMenu: {
			handler(newV) {
				if (newV) {
					this.preventUpdateEditNode = true; // prevent from sending to backend until menu closes
				}
				if (!newV && this.valuesFromDataInputMenuToUpdate.length > 0) {
					this.preventUpdateEditNode = false;
					this.updateConstantValueFromDataInputMenu(this.valuesFromDataInputMenuToUpdate);
					this.valuesFromDataInputMenuToUpdate = [];
				}
			},
		},
	},
};
</script>
<style lang="scss" scoped>
.add-number-optional-fields {
	font-size: 12px;
}
.more-options {
	cursor: pointer;
	.add-optional-fields {
		font-size: 12px;
		text-decoration: underline;
		color: rgb(95, 143, 241);
	}
	.arrow {
		font-size: 16px;
		color: rgb(95, 143, 241);
	}
}
</style>
